package er.prateek.crossly.ui.components

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.layout
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith

/** Compact icon + value pill used for stars, streaks, hints, mistakes. */
@Composable
fun StatChip(
    value: String,
    modifier: Modifier = Modifier,
    label: String? = null,
    tint: Color? = null,
    icon: (@Composable (Color) -> Unit)? = null,
) {
    val colors = MaterialTheme.game
    val accent = tint ?: colors.accent
    Row(
        modifier = modifier
            .clip(RoundedCornerShape(Radius.chip))
            .background(accent.copy(alpha = 0.12f))
            .border(1.dp, accent.copy(alpha = 0.28f), RoundedCornerShape(Radius.chip))
            .padding(horizontal = Spacing.md, vertical = Spacing.sm),
        horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        icon?.invoke(accent)
        Text(text = value, style = MaterialTheme.typography.labelLarge, color = colors.ink)
        label?.let {
            Text(text = it, style = MaterialTheme.typography.labelSmall, color = colors.inkMuted)
        }
    }
}

/**
 * Streak counter. A live streak breathes — a slow scale pulse on the flame —
 * because the streak is the one number the game wants players to protect.
 */
@Composable
fun StreakBadge(
    streak: Int,
    modifier: Modifier = Modifier,
    iconSize: Dp = 18.dp,
) {
    val colors = MaterialTheme.game
    val alive = streak > 0
    val transition = rememberInfiniteTransition(label = "streak")
    val pulse = if (alive) {
        transition.animateFloat(
            initialValue = 1f,
            targetValue = 1.16f,
            animationSpec = infiniteRepeatable(tween(1100), RepeatMode.Reverse),
            label = "streakPulse",
        ).value
    } else {
        1f
    }

    StatChip(
        value = streak.toString(),
        modifier = modifier,
        tint = if (alive) colors.flame else colors.inkMuted,
        icon = { tint ->
            IconFlame(
                size = iconSize,
                tint = if (alive) tint else colors.locked,
                coreTint = if (alive) colors.gold else null,
                modifier = Modifier.graphicsLayer {
                    scaleX = pulse
                    scaleY = pulse
                },
            )
        },
    )
}

/**
 * Segmented XP-style progress bar. Animates to new values so completing a level
 * visibly advances the tier bar when the player lands back on level select.
 */
@Composable
fun ProgressBar(
    progress: Float,
    modifier: Modifier = Modifier,
    height: Dp = 12.dp,
    brush: Brush? = null,
    trackColor: Color? = null,
) {
    val colors = MaterialTheme.game
    val target = progress.coerceIn(0f, 1f)
    val animated by animateFloatAsState(target, tween(650), label = "progress")

    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(height)
            .clip(RoundedCornerShape(Radius.chip))
            .background(trackColor ?: colors.ink.copy(alpha = 0.10f)),
    ) {
        Box(
            modifier = Modifier
                .fillMaxHeight()
                // Measured against the track's own width, so the fill is exact at
                // any container size instead of relying on a fractional weight.
                .layout { measurable, constraints ->
                    val width = (constraints.maxWidth * animated).toInt()
                    val placeable = measurable.measure(
                        constraints.copy(minWidth = width, maxWidth = width),
                    )
                    layout(placeable.width, placeable.height) { placeable.place(0, 0) }
                }
                .clip(RoundedCornerShape(Radius.chip))
                .background(brush ?: colors.accentBrush),
        )
    }
}

/** Labelled progress row: "Easy · 4 / 12" above the bar. */
@Composable
fun LabeledProgress(
    label: String,
    value: String,
    progress: Float,
    modifier: Modifier = Modifier,
    tint: Color? = null,
) {
    val colors = MaterialTheme.game
    val accent = tint ?: colors.accent
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(Spacing.sm)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Text(label, style = MaterialTheme.typography.labelMedium, color = colors.inkMuted)
            Text(value, style = MaterialTheme.typography.labelMedium, color = accent)
        }
        ProgressBar(
            progress = progress,
            brush = Brush.horizontalGradient(listOf(accent, accent.mixWith(colors.accentAlt, 0.5f))),
        )
    }
}
